import { createTheme, outlinedInputClasses } from '@mui/material'
// The three `themeAugmentation` imports are side-effect-only: `MuiChartsTooltip`,
// `MuiDataGrid` and `MuiPickersOutlinedInput` are not core MUI slots, and the
// `declare module` that adds each to `Components` only loads if its file is imported.
// Drop any of them and the matching override below stops compiling (TS2353).
import type {} from '@mui/x-charts/themeAugmentation'
import type {} from '@mui/x-data-grid-pro/themeAugmentation'
import { pickersOutlinedInputClasses } from '@mui/x-date-pickers'
import type {} from '@mui/x-date-pickers/themeAugmentation'

import {
  INPUT_BORDER,
  INPUT_RADIUS_PX,
  IPS_AQUA,
  IPS_METRO_BLUE,
  NAME_HEADING_MAX_WIDTH_PX,
} from '@/common/constants'

const theme = createTheme({
  palette: {
    primary: {
      main: IPS_METRO_BLUE,
      light: IPS_AQUA,
    },
    info: {
      main: IPS_METRO_BLUE,
      light: IPS_AQUA,
    },
  },
  components: {
    // Fields carry their own white fill and a solid neutral outline rather than
    // MUI's transparent-with-translucent-border default, so they read as controls
    // on the app's grey bar and off-white cards alike. Padding is deliberately
    // untouched — `authentication/IconField` overlays its icon on the input's own
    // left padding.
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: INPUT_RADIUS_PX,
          backgroundColor: theme.palette.common.white,
          [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
            borderColor: theme.palette.primary.main,
          },
        }),
        notchedOutline: { borderColor: INPUT_BORDER },
      },
    },
    // The same treatment for date fields, which do NOT go through `MuiOutlinedInput`.
    // With `enableAccessibleFieldDOMStructure` (the v8 default) MUI X renders
    // `PickersTextField` -> `MuiPickersOutlinedInput`, a separate component with its own
    // styled base: 4px `shape.borderRadius`, transparent fill, an `rgba(0, 0, 0, 0.23)`
    // outline and a near-black `text.primary` hover. Left alone, a date field sits next to
    // a themed `Select` at half the radius with no fill. The two blocks look redundant but
    // target different components — don't consolidate them.
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: INPUT_RADIUS_PX,
          backgroundColor: theme.palette.common.white,
          [`&:hover .${pickersOutlinedInputClasses.notchedOutline}`]: {
            borderColor: theme.palette.primary.main,
          },
        }),
        notchedOutline: { borderColor: INPUT_BORDER },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: { borderRadius: INPUT_RADIUS_PX },
      },
    },
    // A chart tooltip's axis header - the shape name on a by-shape chart - renders as
    // the tooltip table's `<caption>`, and x-charts ships that caption
    // `white-space: nowrap` (ChartsTooltip/ChartsTooltipTable.js). A shape name can be a
    // 100+ character comma-separated list inside a single name, so the tooltip grew past
    // the viewport instead of wrapping. Bound it and let it wrap.
    //
    // `whiteSpace: 'normal'` is load-bearing: `maxWidth` alone does nothing against the
    // base `nowrap`. `overflowWrap: 'anywhere'` rather than `break-word` because only
    // `anywhere` lowers the caption's min-content width, which is what lets the
    // shrink-to-fit table honour the cap.
    //
    // This one rule reaches every chart. The default `ChartsTooltip` and enforcement's
    // own `ChartTooltipWithTotal` slot both render their header inside the same
    // `ChartsTooltipTable`, and `@mui/x-charts-pro` re-exports `ChartsTooltip` from
    // `@mui/x-charts` rather than shipping its own - so neither needs a local fix.
    //
    // Two traps. `chartsTooltipClasses.axisValueCell` looks like the right key and is
    // never applied by any component, so an override on it silently does nothing. And
    // `paper` is deliberately left uncapped: the label cells are `nowrap`, so a hard cap
    // on the container would make a wide compare table spill outside its own border -
    // bounding the caption bounds the table.
    MuiChartsTooltip: {
      styleOverrides: {
        table: {
          '& caption': {
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            maxWidth: NAME_HEADING_MAX_WIDTH_PX,
            // Height is bounded too, for the same reason as `MAP_POPUP_TITLE_SX`: wrapping
            // alone turned the Bloomington name into a thirteen-line wall above the values
            // the tooltip is there to show. Two lines, then an ellipsis.
            //
            // This does change the caption's `display` away from `table-caption`, so it is
            // laid out as an ordinary box rather than the table's caption band. It keeps its
            // padding and bottom border and stays the first thing in the tooltip, so it
            // should look unchanged - worth a glance at a by-shape tooltip if this band ever
            // looks off.
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
          },
        },
      },
    },
    // The data grid tints its column resize handle (hover and drag) with
    // `palette.primary.dark`, a darkened teal that reads green next to the rest
    // of the UI. Point the grid's accent variable at the brand blue instead —
    // the separator is its only consumer. `&&` outranks the variable defaults
    // the grid injects on the same root class.
    MuiDataGrid: {
      styleOverrides: {
        root: {
          '&&': { '--DataGrid-t-color-foreground-accent': IPS_METRO_BLUE },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        // These slots render as bare <p> elements with no margin of their own, so they pick up
        // `p { margin: 0 0 1em }` from the vendored semantic base (scss/vendor/semantic-site.css).
        // The toolbar centres each item's margin box, which lifts the label above the buttons.
        displayedRows: { margin: 0 },
        selectLabel: { margin: 0 },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          ...(ownerState.elevation === 3 && {
            boxShadow: 'none',
            border: `1px solid ${theme.palette.divider}`,
          }),
        }),
      },
    },
  },
})

export default theme
